import React, { useEffect, useState } from 'react'
import api from '../api/client'
import type { VolunteerRequest } from '../types'
import { CheckCircle, XCircle, Mail, User, Clock, Phone, FileText } from 'lucide-react'
import { Alert, Avatar, Button, Card, StatusBadge, Switch } from '@rallyshift/design-system'
import { useLocale } from '../contexts/LocaleContext'
import { useAuth } from '../contexts/AuthContext'
import { statusLabel } from '../utils/status'

interface PendingVolunteer extends VolunteerRequest {
  auth_email?: string
  user_created_at?: string
}

const VolunteerApprovalPage: React.FC = () => {
  const { t } = useLocale()
  const { isAdmin } = useAuth()
  const [pendingVolunteers, setPendingVolunteers] = useState<PendingVolunteer[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [autoApprovalEnabled, setAutoApprovalEnabled] = useState(false)
  const [settingsLoading, setSettingsLoading] = useState(false)

  useEffect(() => {
    fetchPendingVolunteers()
    if (isAdmin) {
      fetchAutoApprovalSetting()
    }
  }, [isAdmin]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPendingVolunteers = async () => {
    try {
      const data = await api.volunteerRequests.listPending()
      setPendingVolunteers(data || [])
    } catch (error: any) {
      console.error('Error fetching pending volunteers:', error)
      setError(t('volunteerApproval.loadError'))
    } finally {
      setLoading(false)
    }
  }

  const fetchAutoApprovalSetting = async () => {
    try {
      const data = await api.adminSettings.getAutoApproval()
      setAutoApprovalEnabled(data?.enabled ?? false)
    } catch (error: any) {
      console.error('Error fetching auto-approval setting:', error)
    }
  }

  const toggleAutoApproval = async (next: boolean) => {
    setSettingsLoading(true)
    try {
      await api.adminSettings.setAutoApproval(next)
      setAutoApprovalEnabled(next)
      setSuccess(
        next
          ? t('volunteerApproval.autoApprovalEnabled')
          : t('volunteerApproval.autoApprovalDisabled'),
      )
    } catch (error: any) {
      console.error('Error updating auto-approval setting:', error)
      setError(t('volunteerApproval.autoApprovalToggleError'))
    } finally {
      setSettingsLoading(false)
    }
  }

  const sendApprovalEmail = async (volunteer: PendingVolunteer) => {
    try {
      await api.notifications.send('application_approved', {
        email: volunteer.email,
        firstName: volunteer.first_name,
        lastName: volunteer.last_name,
        motivation: volunteer.motivation || '',
        appliedAt: volunteer.requested_at,
      })
      return true
    } catch (error) {
      console.error('Error sending approval email:', error)
      return false
    }
  }

  const approveVolunteer = async (volunteer: PendingVolunteer) => {
    setActionLoading(volunteer.id)
    setError('')
    setSuccess('')

    try {
      await api.volunteerRequests.approve(volunteer.id)

      const emailSent = await sendApprovalEmail(volunteer)
      const name = `${volunteer.first_name} ${volunteer.last_name}`
      setSuccess(
        emailSent
          ? t('volunteerApproval.approvedWithEmail').replace('{name}', name)
          : t('volunteerApproval.approvedWithoutEmail').replace('{name}', name),
      )
      await fetchPendingVolunteers()
    } catch (error: any) {
      console.error('Error approving volunteer:', error)
      setError(t('volunteerApproval.approveError').replace('{error}', error.message))
    } finally {
      setActionLoading(null)
    }
  }

  const rejectVolunteer = async (volunteer: PendingVolunteer) => {
    setActionLoading(volunteer.id)
    setError('')
    setSuccess('')

    try {
      await api.volunteerRequests.reject(volunteer.id, 'Rejected by admin')
      setSuccess(
        t('volunteerApproval.rejected').replace(
          '{name}',
          `${volunteer.first_name} ${volunteer.last_name}`,
        ),
      )
      await fetchPendingVolunteers()
    } catch (error: any) {
      console.error('Error rejecting volunteer:', error)
      setError(t('volunteerApproval.rejectError').replace('{error}', error.message))
    } finally {
      setActionLoading(null)
    }
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-rs-spin rounded-full border-2.5 border-action-primary border-r-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="font-display text-display-lg text-text-primary">
            {t('volunteerApproval.title')}
          </h1>
          <p className="mt-2 text-md text-text-secondary">
            {pendingVolunteers.length === 0
              ? t('volunteerApproval.allProcessed')
              : `${pendingVolunteers.length} aanmelding(en) wachten op je beoordeling.`}
          </p>
        </div>

        {isAdmin && (
          <Card className="sm:min-w-[280px]" title={t('volunteerApproval.autoApproval')}>
            <Switch
              checked={autoApprovalEnabled}
              disabled={settingsLoading}
              onChange={toggleAutoApproval}
              label={
                autoApprovalEnabled
                  ? t('volunteerApproval.enabled')
                  : t('volunteerApproval.disabled')
              }
            />
          </Card>
        )}
      </div>

      {error && (
        <Alert variant="danger" onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onDismiss={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {pendingVolunteers.length === 0 ? (
        <Card>
          <div className="py-12 text-center">
            <User className="mx-auto h-12 w-12 text-text-muted" />
            <h3 className="mt-2 text-md font-semibold text-text-primary">
              {t('volunteerApproval.noPending')}
            </h3>
            <p className="mt-1 text-sm text-text-secondary">
              {t('volunteerApproval.allProcessed')}
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {pendingVolunteers.map((volunteer) => {
            const name = `${volunteer.first_name ?? ''} ${volunteer.last_name ?? ''}`.trim()
            const busy = actionLoading === volunteer.id

            return (
              <Card key={volunteer.id}>
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <Avatar name={name || volunteer.email} size={48} />
                    <div className="min-w-0">
                      <h3 className="truncate font-display text-display-sm text-text-primary">
                        {name || volunteer.email}
                      </h3>
                      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-text-muted">
                        <span className="flex items-center">
                          <Mail className="mr-1 h-4 w-4" />
                          {volunteer.email}
                        </span>
                        <span className="flex items-center">
                          <Clock className="mr-1 h-4 w-4" />
                          {t('volunteerApproval.applied')}{' '}
                          {new Date(volunteer.requested_at).toLocaleDateString('nl-NL')}
                        </span>
                      </div>
                    </div>
                  </div>
                  <StatusBadge
                    dimension="volunteer"
                    status="pending"
                    label={statusLabel('volunteer', 'pending')}
                  />
                </div>

                {volunteer.phone_number && (
                  <div className="mb-3 flex items-center text-sm text-text-secondary">
                    <Phone className="mr-2 h-4 w-4" />
                    {volunteer.phone_number}
                  </div>
                )}

                {volunteer.motivation && (
                  <div className="mb-4">
                    <div className="mb-2 flex items-center text-sm font-semibold text-text-primary">
                      <FileText className="mr-2 h-4 w-4" />
                      {t('volunteerApproval.motivation')}
                    </div>
                    <div className="max-h-32 overflow-y-auto rounded-md bg-surface-sunken p-3 text-sm text-text-secondary">
                      {volunteer.motivation}
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    fullWidth
                    loading={busy}
                    onClick={() => approveVolunteer(volunteer)}
                    icon={<CheckCircle className="h-4 w-4" />}
                  >
                    {t('volunteerApproval.approve')}
                  </Button>
                  <Button
                    fullWidth
                    variant="secondary"
                    disabled={busy}
                    onClick={() => rejectVolunteer(volunteer)}
                    icon={<XCircle className="h-4 w-4" />}
                  >
                    {t('volunteerApproval.reject')}
                  </Button>
                </div>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default VolunteerApprovalPage
